import * as React from 'react';
import SelectField from 'react-md/lib/SelectFields';
import { ChildcareCentreDetails } from '../Calculation/types';

interface Props {
    allCentres: ChildcareCentreDetails[];
    selectedCentre?: ChildcareCentreDetails;
    onSelectedCentreChanged: (selected?: ChildcareCentreDetails) => void;
}

interface SelectorState {
    selectedState?: string;
}

interface SelectFieldItem {
    value: string | number;
    label: string;
}

export class SelectChildcareCentre extends React.Component<Props & React.HTMLAttributes<HTMLDivElement>, SelectorState> {
    public static getDerivedStateFromProps(props: Readonly<Props>, state: SelectorState): Partial<SelectorState> | null {
        const stateNames = SelectChildcareCentre.getDistinctStateNames(props.allCentres);

        let selectedCentreState: string | undefined;
        if (props.selectedCentre) {
            selectedCentreState = props.selectedCentre.state;
        } else if (stateNames.length === 1) {
            selectedCentreState = stateNames[0];
        }

        if (state.selectedState === selectedCentreState) {
            return null;
        }

        return {
            selectedState: state.selectedState || selectedCentreState,
        }
    }

    private static getDistinctStateNames(centres: ChildcareCentreDetails[]): string[] {
        const stateNames: string[] = centres.reduce((states: string[], centre: ChildcareCentreDetails) => {
            if (states.indexOf(centre.state) === -1) {
                states.push(centre.state);
            }

            return states;
        }, [] as string[]);

        stateNames.sort();

        return stateNames;
    }

    private static filterCentresForState(centres: ChildcareCentreDetails[], state: string) {
        return centres.filter(centre => centre.state === state);
    }

    public constructor(props: Props) {
        super(props);

        this.state = {
        };
    }

    public render() {
        const stateSelection = this.renderStateSelection();
        const centreSelection = this.renderCentreSelection();
        return (
            <div className={this.props.className}>
                <div className="childcare-centre-selector">
                    {stateSelection}
                    {centreSelection}
                </div></div>
                );
    }

    private renderStateSelection(): JSX.Element {
        const stateNames = SelectChildcareCentre.getDistinctStateNames(this.props.allCentres);

        const stateMenuItems: SelectFieldItem[] = stateNames.map(stateName => ({
            label: stateName,
            value: stateName
        }));

        // Don't show state selector if there's only one to choose from
        if (stateNames.length === 1) {
            return <div></div>;
        }

        const selectedItem = this.state.selectedState && stateMenuItems.find(item => item.value === this.state.selectedState);

        return <div>
            <SelectField className="md-cell" id="childcare-centre-state" menuItems={stateMenuItems} placeholder="Select your state" value={selectedItem ? selectedItem.value : undefined} onChange={this.childcareCentreStateChanged} />
            <br />
        </div>
    }

    private renderCentreSelection(): JSX.Element {
        const { selectedState } = this.state

        const allCentres = this.props.allCentres.slice(0);
        allCentres.sort((centreA, centreB) => {
            const valA = centreA.sortOrder === undefined ? Number.MAX_SAFE_INTEGER : centreA.sortOrder;
            const valB = centreB.sortOrder === undefined ? Number.MAX_SAFE_INTEGER : centreB.sortOrder;

            if (valA === valB) {
                return 0;
            }

            return valA < valB ? -1 : 1;
        });

        const centreMenuItems: SelectFieldItem[] = allCentres.filter(centre => centre.state === selectedState).map(centre => ({
            label: centre.displayName,
            value: centre.id
        }));

        const selectedItem = this.props.selectedCentre && centreMenuItems.find(item => item.value === this.props.selectedCentre!.id);

        return (
            <SelectField className="md-cell" value={selectedItem ? selectedItem.value : undefined} disabled={this.state.selectedState === undefined} id="childcare-centre-select" menuItems={centreMenuItems} placeholder="Select your centre" onChange={this.childcareCentreChanged} />
        )
    }

    private childcareCentreStateChanged = (selectedState: string) => {
        const filteredCentres = SelectChildcareCentre.filterCentresForState(this.props.allCentres, selectedState);
        const selectedCentre = this.props.selectedCentre && filteredCentres.find(centre => centre.id === this.props.selectedCentre!.id);

        this.setState({selectedState}, () => {
            this.props.onSelectedCentreChanged(selectedCentre);
        });
    }

    private childcareCentreChanged = (centreId: number) => {
        const centre = this.props.allCentres.find(c => c.id === centreId);
        this.props.onSelectedCentreChanged(centre);
    }
}